import React, { useContext, useEffect, useState } from "react";
import { Button, Input, Select, Checkbox, Form } from "antd";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { ProductContext } from "context/ProductContext";
import axios from "axios"; // For API calls

const { Option } = Select;
const { TextArea } = Input;

const Info = ({ nextStep }) => {
  const { productData, setProductData, next } = useContext(ProductContext);

  console.log("Product data===", productData)
  const [subClasses, setSubClasses] = useState([]);
  const [coverTypes, setCoverTypes] = useState([]);
  const [rates, setRates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [policyNoConfigs, setPolicyNoConfigs] = useState([]);
  const [policyDeliveries, setPolicyDeliveries] = useState([]);
  const [claimConfigs, setClaimConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    subclass: Yup.string().required("Subclass is required"),
    covertype_code: Yup.string().required("Cover type is required"),
    product_ref: Yup.string().required("Internal Reference is required"),
    product_name: Yup.string().required("Product Name is required"),
    minimum_premium: Yup.number()
      .required("Minimum Premium is required")
      .positive("Must be a positive number"),
    policy_term: Yup.string().required("Policy Term is required"),
    rate_code: Yup.string().required("Rate is required"),
    commission_code: Yup.string().required("Commission is required"),
    workflow_code: Yup.string().required("Purchase Workflow is required"),
    system_policy_delivery_ref: Yup.string().required("Policy Delivery is required"),
    policy_no_config_system_ref: Yup.string().required("Policy No. Config is required"),
    system_claim_config_ref: Yup.string().required("Claim Config is required"),
    description: Yup.string().required("Product Description is required"),
    preferred_currency: Yup.array().min(1, "At least one currency must be selected"),
  });

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Replace with your API endpoints
        const [
          subClassesRes,
          ratesRes,
          commissionsRes,
          workflowsRes,
          policyNoConfigsRes,
          policyDeliveriesRes,
          claimConfigsRes,
        ] = await Promise.all([
          axios.get("/v1/api/subclass/list_summary"),
          axios.get("/v1/api/rate/list"),
          axios.get("/v1/api/commission/list"),
          axios.get("/v1/api/workflow/list"),
          axios.get("/v1/api/policy_no_config/list"),
          axios.get("/v1/api/policy_delivery/list"),
          axios.get("/v1/api/claim_config/list"),
        ]);

        setSubClasses(subClassesRes.data.result.underwritter_subclasses || []);
        setRates(ratesRes.data.result.rates || []);
        setCommissions(ratesRes.data.result.commission_configs || []);
        setWorkflows(workflowsRes.data.result.workflows || []);
        setPolicyNoConfigs(policyNoConfigsRes.data.result.policy_no_configs || []);
        setPolicyDeliveries(policyDeliveriesRes.data.result.policy_deliveries || []);
        setClaimConfigs(claimConfigsRes.data.result.claim_configs || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle subclass change to update cover types
  const handleSubClassChange = (value, setFieldValue) => {
    const selectedSubClass = subClasses.find((subClass) => subClass.underwritter_class_code === value);
    setCoverTypes(selectedSubClass?.covertypes || []);
    setFieldValue("subclass", value);
    setFieldValue("covertype_code", ""); // Reset cover type when subclass changes
    setProductData({  subclass: value });
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const updatedProductData = {
        //...productData,
        ...values,
        underwritter_subclass_code: values.subclass,
      };
      setProductData(updatedProductData);

      // API call to create product
      await axios.post("/v1/api/product/add", updatedProductData);
      nextStep(); // Proceed to the next step
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="product-information">
      <h2>Product Information</h2>
      <Formik
        initialValues={{
          subclass: productData?.subclass || "",
          covertype_code: productData?.covertype_code || "",
          product_ref: productData?.product_ref || "",
          product_name: productData?.product_name || "",
          minimum_premium: productData?.minimum_premium || "",
          policy_term: productData?.policy_term || "",
          rate_code: productData?.rate_code || "",
          commission_code: productData?.commission_code || "",
          workflow_code: productData?.workflow_code || "",
          system_policy_delivery_ref: productData?.system_policy_delivery_ref || "",
          policy_no_config_system_ref: productData?.policy_no_config_system_ref || "",
          system_claim_config_ref: productData?.system_claim_config_ref || "",
          description: productData?.description || "",
          preferred_currency: productData?.preferred_currency || [],
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <FormikForm>
            <Form layout="vertical" className="row">
              {/* Subclass and Cover Type */}
              <div className="col-md-6 mb-3">
                <Form.Item label="Select Subclass" validateStatus={errors.subclass && touched.subclass ? "error" : ""} help={<ErrorMessage name="subclass" />}>
                  <Field name="subclass">
                    {({ field }) => (
                      <Select
                        {...field}
                        onChange={(value) => handleSubClassChange(value, setFieldValue)}
                        placeholder="Select a subclass"
                        loading={loading}
                      >
                        {subClasses.map((subClass) => (
                          <Option key={subClass.underwritter_class_code} value={subClass.underwritter_class_code}>
                            {subClass.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Item label="Select Cover Type" validateStatus={errors.covertype_code && touched.covertype_code ? "error" : ""} help={<ErrorMessage name="covertype_code" />}>
                  <Field name="covertype_code">
                    {({ field }) => (
                      <Select
                        {...field}
                        onChange={(value) => setFieldValue("covertype_code", value)}
                        placeholder="Select a cover type"
                        disabled={!values.subclass}
                        loading={loading}
                      >
                        {coverTypes.map((coverType) => (
                          <Option key={coverType.code} value={coverType.code}>
                            {coverType.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>

              {/* Internal Reference and Product Name */}
              <div className="col-md-6 mb-3">
                <Form.Item label="Internal Reference" validateStatus={errors.product_ref && touched.product_ref ? "error" : ""} help={<ErrorMessage name="product_ref" />}>
                  <Field name="product_ref">
                    {({ field }) => <Input {...field} placeholder="Internal Ref" />}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Item label="Product Name" validateStatus={errors.product_name && touched.product_name ? "error" : ""} help={<ErrorMessage name="product_name" />}>
                  <Field name="product_name">
                    {({ field }) => <Input {...field} placeholder="Name" />}
                  </Field>
                </Form.Item>
              </div>

              {/* Minimum Premium and Policy Term */}
              <div className="col-md-6 mb-3">
                <Form.Item label="Minimum Premium" validateStatus={errors.minimum_premium && touched.minimum_premium ? "error" : ""} help={<ErrorMessage name="minimum_premium" />}>
                  <Field name="minimum_premium">
                    {({ field }) => <Input {...field} type="number" placeholder="Minimum Premium" />}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Item label="Policy Term" validateStatus={errors.policy_term && touched.policy_term ? "error" : ""} help={<ErrorMessage name="policy_term" />}>
                  <Field name="policy_term">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("policy_term", value)} placeholder="Select Policy Term">
                        <Option value="DAILY">Daily</Option>
                        <Option value="WEEKLY">Weekly</Option>
                        <Option value="MONTHLY">Monthly</Option>
                        <Option value="YEARLY">Yearly</Option>
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>

              {/* Rate, Commission, Workflow */}
              <div className="col-md-4 mb-3">
                <Form.Item label="Rate" validateStatus={errors.rate_code && touched.rate_code ? "error" : ""} help={<ErrorMessage name="rate_code" />}>
                  <Field name="rate_code">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("rate_code", value)} placeholder="Select Rate" loading={loading}>
                        {rates.map((rate) => (
                          <Option key={rate.rate_code} value={rate.rate_code}>
                            {rate.rate_name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Item label="Commission" validateStatus={errors.commission_code && touched.commission_code ? "error" : ""} help={<ErrorMessage name="commission_code" />}>
                  <Field name="commission_code">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("commission_code", value)} placeholder="Select Commission" loading={loading}>
                        {commissions.map((commission) => (
                          <Option key={commission.commission_code} value={commission.commission_code}>
                            {commission.commission_name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Item label="Purchase Workflow" validateStatus={errors.workflow_code && touched.workflow_code ? "error" : ""} help={<ErrorMessage name="workflow_code" />}>
                  <Field name="workflow_code">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("workflow_code", value)} placeholder="Select Workflow" loading={loading}>
                        {workflows.map((workflow) => (
                          <Option key={workflow.code} value={workflow.code}>
                            {workflow.name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>

              {/* Policy Delivery, Policy No. Config, Claim Config */}
              <div className="col-md-4 mb-3">
                <Form.Item label="Policy Delivery" validateStatus={errors.system_policy_delivery_ref && touched.system_policy_delivery_ref ? "error" : ""} help={<ErrorMessage name="system_policy_delivery_ref" />}>
                  <Field name="system_policy_delivery_ref">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("system_policy_delivery_ref", value)} placeholder="Select Policy Delivery" loading={loading}>
                        {policyDeliveries.map((delivery) => (
                          <Option key={delivery.system_policy_delivery_ref} value={delivery.system_policy_delivery_ref}>
                            {delivery.policy_delivery_name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Item label="Policy No. Config" validateStatus={errors.policy_no_config_system_ref && touched.policy_no_config_system_ref ? "error" : ""} help={<ErrorMessage name="policy_no_config_system_ref" />}>
                  <Field name="policy_no_config_system_ref">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("policy_no_config_system_ref", value)} placeholder="Select Policy No. Config" loading={loading}>
                        {policyNoConfigs.map((config) => (
                          <Option key={config.policy_no_config_system_ref} value={config.policy_no_config_system_ref}>
                            {config.policy_no_name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-4 mb-3">
                <Form.Item label="Claim Config" validateStatus={errors.system_claim_config_ref && touched.system_claim_config_ref ? "error" : ""} help={<ErrorMessage name="system_claim_config_ref" />}>
                  <Field name="system_claim_config_ref">
                    {({ field }) => (
                      <Select {...field} onChange={(value) => setFieldValue("system_claim_config_ref", value)} placeholder="Select Claim Config" loading={loading}>
                        {claimConfigs.map((config) => (
                          <Option key={config.system_claim_config_ref} value={config.system_claim_config_ref}>
                            {config.claim_config_name}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Field>
                </Form.Item>
              </div>

              {/* Preferred Currency and Description */}
              <div className="col-md-6 mb-3">
                <Form.Item label="Preferred Currency" validateStatus={errors.preferred_currency && touched.preferred_currency ? "error" : ""} help={<ErrorMessage name="preferred_currency" />}>
                  <Field name="preferred_currency">
                    {({ field }) => (
                      <Checkbox.Group
                        {...field}
                        onChange={(checkedValues) => setFieldValue("preferred_currency", checkedValues)}
                        options={[
                          { label: "KES", value: "KES" },
                          { label: "USD", value: "USD" },
                        ]}
                      />
                    )}
                  </Field>
                </Form.Item>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Item label="Product Description" validateStatus={errors.description && touched.description ? "error" : ""} help={<ErrorMessage name="description" />}>
                  <Field name="description">
                    {({ field }) => <TextArea {...field} placeholder="Description" rows={3} />}
                  </Field>
                </Form.Item>
              </div>

              {/* Submit Button */}
              <div className="col-12 mt-4">
                <Button type="primary"  onClick={next} htmlType="submit" loading={loading} block>
                  Continue
                </Button>
              </div>
            </Form>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default Info;